<template>
  <div class="flex-column" :class="hasTime ? ['flex-sm-row', 'align-items-sm-center'] : null">
    <div class="d-flex flex-wrap">
      <div>
        <label>
          <span v-if="label" class="mr-1">{{ label }}</span>
          <date-picker
            v-model="date"
            :min="min"
            :max="max"
            :range="hasRange"
            value-type="YYYY-MM-DD"
            :disabled="disabled"
            :disabled-date="disabledDate"
            :shortcuts="
              hasShortcuts || customShortcuts ? (customShortcuts ? customShortcuts : shortcuts) : []
            "
            :placeholder="placeholder"
            class="mr-1"
            :lang="lang"
            @blur="handleTouched"
            @close="handleTouched"
            @clear="handleClear"
            :open.sync="isDateOpen"
            :editable="!isPhone"
          >
            <!-- mobile only -->
            <template v-slot:sidebar v-if="hasShortcuts">
              <slot name="dateSidebar">
                <img
                  src="@/assets/img/close.svg"
                  alt="close icon"
                  class="close app--hidden app--phone-shown"
                  @click="closeDatePicker"
                />
              </slot>
            </template>
            <template v-else v-slot:header>
              <slot name="dateDeader">
                <img
                  src="@/assets/img/close.svg"
                  alt="close icon"
                  class="close app--hidden app--phone-shown"
                  @click="closeDatePicker"
                />
              </slot>
            </template>
            <!-- The End of mobile only -->
            <template v-slot:icon-calendar>
              <slot name="icon">
                <img src="../assets/img/icon/calendar.svg" alt="Date Picker" />
              </slot>
            </template>
          </date-picker>
        </label>
      </div>
      <template v-if="hasTime">
        <div>
          <date-picker
            v-model="time"
            type="time"
            placeholder="HH:mm:ss"
            value-type="format"
            :use12h="use12h"
            :disabled="disabled"
            class="mr-1"
            :open.sync="isTimeOpen"
            @blur="handleTouched"
            @close="handleTouched"
            :editable="!isPhone"
          >
            <template v-slot:header>
              <slot name="timeHeader">
                <img
                  src="@/assets/img/close.svg"
                  class="close app--hidden app--phone-shown"
                  @click="closeTimePicker"
                />
              </slot>
            </template>
            <template v-slot:icon-calendar>
              <img src="../assets/img/icon/clock.svg" alt="Time Picker" />
            </template>
          </date-picker>
        </div>
      </template>
      <div class="mask" v-if="!disabled && (isDateOpen || isTimeOpen)"></div>
    </div>
  </div>
</template>

<script>
import { scheduled, asyncScheduler } from 'rxjs';
import {
  tap, map, filter, startWith, distinctUntilChanged, mergeAll,
} from 'rxjs/operators';
import DatePicker from 'vue2-datepicker';
import {
  getMonthStartDate,
  getMonthEndDate,
  getLastMonthStartDate,
  getLastMonthEndDate,
  addDays,
} from '@/helpers/date';
import 'vue2-datepicker/locale/zh-tw';
import 'vue2-datepicker/locale/en';

const now = new Date();
const nowYear = now.getFullYear();

export default {
  components: {
    DatePicker,
  },
  props: {
    value: [String, Array],
    label: String,
    disabled: Boolean,
    hasTime: Boolean,
    min: {
      type: Date,
      default: null,
    },
    max: {
      type: Date,
      default: null,
    },
    use12h: {
      type: Boolean,
      default: true,
    },
    hasRange: {
      type: Boolean,
      default: false,
    },
    // If hasRange is true,
    // the returned value in onClick function inside shortcuts object must be [Date, Date]
    customShortcuts: Array,
    hasShortcuts: Boolean,
    placeholder: {
      type: String,
      default: 'YYYY-MM-DD',
    },
    defaultTime: String,
  },
  data: () => ({
    inputVal: null,
    date: null,
    time: '00:00:00',
    isDateOpen: false,
    isTimeOpen: false,
    lang: window.navigator.language === 'en' ? 'en' : 'zh-tw',
    shortcuts: [
      {
        text: '開業至今',
        onClick() {
          return [new Date('2021-01-01'), now];
        },
      },
      {
        text: '本年度',
        onClick() {
          return [new Date(`${nowYear}-01-01`), new Date(`${nowYear}-12-31`)];
        },
      },
      {
        text: '本月',
        onClick() {
          return [getMonthStartDate(), getMonthEndDate()];
        },
      },
      {
        text: '上月',
        onClick() {
          return [getLastMonthStartDate(), getLastMonthEndDate()];
        },
      },
      {
        text: '30日前',
        onClick() {
          return [addDays(now, -30), now];
        },
      },
      {
        text: '90日前',
        onClick() {
          return [addDays(now, -90), now];
        },
      },
    ],
    isPhone: window.innerWidth <= 600,
  }),
  watch: {
    inputVal(val) {
      this.$emit('input', val);
    },
  },
  subscriptions() {
    return {
      value$: this.$watchAsObservable('value').pipe(
        startWith(1),
        filter(() => {
          if (!this.value) {
            if (this.hasTime) {
              this.time = null;
            }
            this.date = null;
          }
          return !!this.value;
        }),
        tap(() => {
          if (this.hasTime && !this.hasRange) {
            const [date, time] = this.value.split(' ');
            this.date = date;
            this.time = time || '00:00:00';
          } else {
            this.date = this.value;
          }
        }),
      ),
      datetime$: scheduled(
        [this.$watchAsObservable('date'), this.$watchAsObservable('time')],
        asyncScheduler,
      ).pipe(
        mergeAll(),
        map(() => {
          if (!this.hasTime || !this.date) return this.date;
          return `${this.date} ${this.time || this.defaultTime || '00:00:00'}`;
        }),
        distinctUntilChanged(),
        tap((datetime) => {
          this.inputVal = datetime;
        }),
      ),
    };
  },
  methods: {
    handleTouched() {
      this.$emit('touched');
    },
    handleChange(value, type) {
      if (type === 'ampm') {
        this.isTimeOpen = false;
      }
    },
    handleClear() {
      this.inputVal = '';
    },
    closeDatePicker() {
      this.isDateOpen = false;
    },
    closeTimePicker() {
      this.isTimeOpen = false;
    },
    disabledDate(date) {
      if (this.max && this.min) {
        return date < this.min || date > this.max;
      }
      if (this.min) {
        return date < this.min;
      }
      if (this.max) {
        return date > this.max;
      }
      return null;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/datepicker";
.mx-datepicker-header {
  border-bottom: none;
  padding-bottom: 0;
}

@include respond(phone) {
  .mx-datepicker-popup {
    top: 50% !important;
    left: 0 !important;
    width: 100% !important;
    position: fixed !important;
    transform: translateY(-50%);
  }
  .mx-calendar,
  .mx-time {
    width: 100%;
  }
  .mx-datepicker-sidebar + .mx-datepicker-content {
    margin-top: 1.1rem;
    margin-bottom: 1.1rem;
  }
  .mx-calendar + .mx-calendar {
    border: none;
  }
}
</style>

<style scoped lang="scss">
.mx-datepicker-range {
  @media (max-width: 1200px) {
    width: 215px;
  }
}

.close {
  margin: 3px 0;
  float: none;
}

::v-deep {
  @include respond(phone) {
    .mx-input-wrapper {
      input {
        width: 6.875rem;
      }
      input {
        font-size: 0.75rem;
      }
    }
    .mx-datepicker-range {
      .mx-input-wrapper {
        input {
          width: 12rem;
        }
      }
    }
    .mx-datepicker {
      width: unset;
    }

    .mask {
        position: fixed;
        top: 0;
        right: 0;
        height: 100vh;
        width: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 111;
    }
  }
}
</style>
